<template>
  <div class="inquiry-form">
    <h2>
      {{
        currentStep === 1 ? "Submit Your Inquiry" : "Complete Your Application"
      }}
    </h2>

    <!-- Step 1: Basic Information -->
    <template v-if="currentStep === 1">
      <form @submit.prevent="goToNextStep" novalidate>
        <div class="form-grid">
          <div class="form-group" :class="{ 'has-error': errors.name }">
            <label for="name">Full Name <span class="required">*</span></label>
            <input
              type="text"
              id="name"
              v-model.trim="formData.name"
              @input="validateName"
              placeholder="William Norton"
              :disabled="submitting"
              required
            />
            <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
          </div>

          <div class="form-group" :class="{ 'has-error': errors.email }">
            <label for="email"
              >Email Address <span class="required">*</span></label
            >
            <input
              type="email"
              id="email"
              v-model.trim="formData.email"
              @input="validateEmail"
              placeholder="william.norton@gmail.com"
              :disabled="submitting"
              required
            />
            <span v-if="errors.email" class="error-text">{{
              errors.email
            }}</span>
          </div>

          <div class="form-group" :class="{ 'has-error': errors.phone }">
            <label for="phone"
              >Phone Number <span class="required">*</span></label
            >
            <input
              type="tel"
              id="phone"
              v-model="formData.phone"
              v-mask="'(###) ###-####'"
              @input="validatePhone"
              placeholder="(555) 123-4567"
              :disabled="submitting"
              required
            />
            <span v-if="errors.phone" class="error-text">{{
              errors.phone
            }}</span>
          </div>
        </div>

        <div class="form-consent" :class="{ 'has-error': errors.consent }">
          <label>
            <input
              type="checkbox"
              v-model="formData.consent"
              @change="validateConsent"
              :disabled="submitting"
              required
            />
            I agree to the processing of data in accordance with the
            <router-link to="/privacy" class="text-orange-500 hover:underline">
              Privacy Policy
            </router-link>
          </label>
          <span v-if="errors.consent" class="error-text">{{
            errors.consent
          }}</span>
        </div>

        <div v-if="submissionError" class="error-message">
          {{ submissionError }}
        </div>

        <button
          type="submit"
          class="launch-button"
          :disabled="submitting || hasErrors"
        >
          <span>{{ submitting ? "Processing..." : "Next Step" }}</span>
        </button>
      </form>
    </template>

    <!-- Step 2: Detailed Application -->
    <template v-else-if="currentStep === 2 && !isSuccess">
      <form @submit.prevent="submitCompleteForm" novalidate>
        <div class="form-grid">
          <!-- Date of Birth -->
          <div class="form-group" :class="{ 'has-error': errors.dateOfBirth }">
            <label for="dateOfBirth"
              >Date of Birth <span class="required">*</span></label
            >
            <input
              type="date"
              id="dateOfBirth"
              v-model="formData.dateOfBirth"
              @input="validateDateOfBirth"
              :disabled="submitting"
              required
            />
            <span v-if="errors.dateOfBirth" class="error-text">{{
              errors.dateOfBirth
            }}</span>
          </div>

          <!-- Social Security Number -->
          <div class="form-group" :class="{ 'has-error': errors.ssn }">
            <label for="ssn">Social Security Number</label>
            <input
              type="text"
              id="ssn"
              v-model="formData.ssn"
              v-mask="'###-##-####'"
              @input="validateSSN"
              placeholder="123-45-6789"
              :disabled="submitting"
            />
            <span v-if="errors.ssn" class="error-text">{{ errors.ssn }}</span>
          </div>

          <!-- Address Fields -->
          <div
            class="form-group full-width"
            :class="{ 'has-error': errors.country }"
          >
            <label for="country">Country <span class="required">*</span></label>
            <input
              type="text"
              id="country"
              v-model.trim="formData.country"
              @input="validateCountry"
              placeholder="United States"
              :disabled="submitting"
              required
            />
            <span v-if="errors.country" class="error-text">{{
              errors.country
            }}</span>
          </div>

          <div
            class="form-group full-width"
            :class="{ 'has-error': errors.addressLine1 }"
          >
            <label for="addressLine1"
              >Address Line 1 <span class="required">*</span></label
            >
            <input
              type="text"
              id="addressLine1"
              v-model.trim="formData.addressLine1"
              @input="validateAddressLine1"
              placeholder="123 Main Street"
              :disabled="submitting"
              required
            />
            <span v-if="errors.addressLine1" class="error-text">{{
              errors.addressLine1
            }}</span>
          </div>

          <div class="form-group full-width">
            <label for="addressLine2">Address Line 2 (Optional)</label>
            <input
              type="text"
              id="addressLine2"
              v-model.trim="formData.addressLine2"
              placeholder="Apt 4B"
              :disabled="submitting"
            />
          </div>

          <div class="form-group" :class="{ 'has-error': errors.city }">
            <label for="city">City <span class="required">*</span></label>
            <input
              type="text"
              id="city"
              v-model.trim="formData.city"
              @input="validateCity"
              placeholder="New York"
              :disabled="submitting"
              required
            />
            <span v-if="errors.city" class="error-text">{{ errors.city }}</span>
          </div>

          <div class="form-group" :class="{ 'has-error': errors.state }">
            <label for="state">State <span class="required">*</span></label>
            <input
              type="text"
              id="state"
              v-model.trim="formData.state"
              @input="validateState"
              placeholder="NY"
              :disabled="submitting"
              required
            />
            <span v-if="errors.state" class="error-text">{{
              errors.state
            }}</span>
          </div>

          <div class="form-group" :class="{ 'has-error': errors.zipCode }">
            <label for="zipCode"
              >ZIP Code <span class="required">*</span></label
            >
            <input
              type="text"
              id="zipCode"
              v-model="formData.zipCode"
              v-mask="'#####'"
              @input="validateZipCode"
              placeholder="10001"
              :disabled="submitting"
              required
            />
            <span v-if="errors.zipCode" class="error-text">{{
              errors.zipCode
            }}</span>
          </div>

          <!-- Driver License Photo Upload -->
          <div
            class="form-group full-width"
            :class="{ 'has-error': errors.driverLicensePhoto }"
          >
            <label for="driverLicensePhoto"
              >Driver License Photo <span class="required">*</span></label
            >
            <div class="file-upload-wrapper">
              <input
                type="file"
                id="driverLicensePhoto"
                ref="fileInput"
                @change="handleFileUpload"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                :disabled="submitting"
                class="file-input"
              />
              <label for="driverLicensePhoto" class="file-upload-label">
                <i class="bi bi-cloud-upload"></i>
                <span v-if="!formData.driverLicensePhoto">
                  Choose file or drag & drop
                </span>
                <span v-else class="file-name">
                  {{ formData.driverLicensePhotoName }}
                </span>
              </label>
              <div v-if="formData.driverLicensePhoto" class="file-preview">
                <img :src="photoPreview" alt="License preview" />
                <button
                  type="button"
                  class="remove-file"
                  @click="removeFile"
                  :disabled="submitting"
                >
                  <i class="bi bi-x-circle"></i>
                </button>
              </div>
            </div>
            <div class="file-info">
              Accepted formats: JPG, PNG, WEBP. Max size: 5MB
            </div>
            <span v-if="errors.driverLicensePhoto" class="error-text">{{
              errors.driverLicensePhoto
            }}</span>
          </div>

          <!-- Residence Type -->
          <div
            class="form-group"
            :class="{ 'has-error': errors.residenceType }"
          >
            <label for="residenceType"
              >Residence Type <span class="required">*</span></label
            >
            <select
              id="residenceType"
              v-model="formData.residenceType"
              @change="validateResidenceType"
              :disabled="submitting"
              required
            >
              <option value="">Select residence type</option>
              <option value="own_no_mortgage">Own without Mortgage</option>
              <option value="paying_rent">Paying Rent</option>
              <option value="living_with_family">Living with Family</option>
              <option value="paying_mortgage">Paying Mortgage</option>
            </select>
            <span v-if="errors.residenceType" class="error-text">{{
              errors.residenceType
            }}</span>
          </div>

          <!-- Mortgage/Rent Payment -->
          <div class="form-group" :class="{ 'has-error': errors.rentPayment }">
            <label for="rentPayment"
              >Mortgage/Rent Payment <span class="required">*</span></label
            >
            <input
              type="number"
              id="rentPayment"
              v-model.number="formData.rentPayment"
              @input="validateRentPayment"
              placeholder="1500"
              :disabled="submitting"
              required
            />
            <span v-if="errors.rentPayment" class="error-text">{{
              errors.rentPayment
            }}</span>
          </div>

          <!-- Employer -->
          <div class="form-group" :class="{ 'has-error': errors.employer }">
            <label for="employer"
              >Employer/Company Name <span class="required">*</span></label
            >
            <input
              type="text"
              id="employer"
              v-model.trim="formData.employer"
              @input="validateEmployer"
              placeholder="ABC Corporation"
              :disabled="submitting"
              required
            />
            <span v-if="errors.employer" class="error-text">{{
              errors.employer
            }}</span>
          </div>

          <!-- Job Title -->
          <div class="form-group" :class="{ 'has-error': errors.jobTitle }">
            <label for="jobTitle"
              >Job Title/Position <span class="required">*</span></label
            >
            <input
              type="text"
              id="jobTitle"
              v-model.trim="formData.jobTitle"
              @input="validateJobTitle"
              placeholder="Software Engineer"
              :disabled="submitting"
              required
            />
            <span v-if="errors.jobTitle" class="error-text">{{
              errors.jobTitle
            }}</span>
          </div>

          <!-- Annual Income -->
          <div class="form-group" :class="{ 'has-error': errors.annualIncome }">
            <label for="annualIncome"
              >Total Annual Gross Income <span class="required">*</span></label
            >
            <input
              type="number"
              id="annualIncome"
              v-model.number="formData.annualIncome"
              @input="validateAnnualIncome"
              placeholder="75000"
              :disabled="submitting"
              required
            />
            <span v-if="errors.annualIncome" class="error-text">{{
              errors.annualIncome
            }}</span>
          </div>
        </div>

        <div v-if="submissionError" class="error-message">
          {{ submissionError }}
        </div>

        <div class="button-group">
          <button
            type="button"
            class="back-button"
            @click="goBackToStep1"
            :disabled="submitting"
          >
            <span>← Back</span>
          </button>
          <button
            type="submit"
            class="launch-button"
            :disabled="submitting || hasErrors"
          >
            <span>{{
              submitting ? "Submitting..." : "Submit Application"
            }}</span>
          </button>
        </div>
      </form>
    </template>

    <!-- Success Section -->
    <template v-else-if="isSuccess">
      <div class="success-section">
        <i class="bi bi-check-circle section-icon"></i>
        <h3>Application Submitted Successfully!</h3>
        <p>
          Thank you! We've received your application and will get back to you
          soon.
        </p>
        <button class="done-button" @click="resetToForm">
          <i class="bi bi-arrow-left"></i>
          <span>Submit Another Application</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js";

export default {
  name: "InquiryForm",
  directives: {
    mask,
  },
  props: {
    carId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      currentStep: 1,
      formData: {
        // Step 1
        name: "",
        email: "",
        phone: "",
        consent: false,
        carId: this.carId,
        // Step 2
        dateOfBirth: "",
        ssn: "",
        country: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        driverLicensePhoto: null,
        driverLicensePhotoName: "",
        residenceType: "",
        rentPayment: null,
        employer: "",
        jobTitle: "",
        annualIncome: null,
      },
      errors: {
        name: "",
        email: "",
        phone: "",
        consent: "",
        dateOfBirth: "",
        ssn: "",
        country: "",
        addressLine1: "",
        city: "",
        state: "",
        zipCode: "",
        driverLicensePhoto: "",
        residenceType: "",
        rentPayment: "",
        employer: "",
        jobTitle: "",
        annualIncome: "",
      },
      photoPreview: null,
      submissionError: null,
      submitting: false,
      isSuccess: false,
      baseUrl: API_CONFIG.baseUrl,
    };
  },
  computed: {
    hasErrors() {
      return Object.values(this.errors).some((error) => error !== "");
    },
  },
  methods: {
    // Step 1 Validations
    validateName() {
      const name = this.formData.name;
      if (!name) {
        this.errors.name = "Full name is required";
      } else if (name.length < 2) {
        this.errors.name = "Name must be at least 2 characters";
      } else if (!/^[a-zA-Z\s-]+$/.test(name)) {
        this.errors.name = "Name can only contain letters, spaces, and hyphens";
      } else {
        this.errors.name = "";
      }
    },
    validateEmail() {
      const email = this.formData.email;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        this.errors.email = "Email is required";
      } else if (!emailRegex.test(email)) {
        this.errors.email = "Please enter a valid email address";
      } else {
        this.errors.email = "";
      }
    },
    validatePhone() {
      const phone = this.formData.phone.replace(/\D/g, "");
      if (!phone) {
        this.errors.phone = "Phone number is required";
      } else if (phone.length !== 10) {
        this.errors.phone = "Please enter a valid 10-digit US phone number";
      } else {
        this.errors.phone = "";
      }
    },
    validateConsent() {
      if (!this.formData.consent) {
        this.errors.consent = "You must agree to the privacy policy";
      } else {
        this.errors.consent = "";
      }
    },

    // Step 2 Validations
    validateDateOfBirth() {
      const dob = this.formData.dateOfBirth;
      if (!dob) {
        this.errors.dateOfBirth = "Date of birth is required";
      } else {
        const age = this.calculateAge(new Date(dob));
        if (age < 18) {
          this.errors.dateOfBirth = "You must be at least 18 years old";
        } else if (age > 120) {
          this.errors.dateOfBirth = "Please enter a valid date of birth";
        } else {
          this.errors.dateOfBirth = "";
        }
      }
    },
    validateSSN() {
      const ssn = this.formData.ssn.replace(/\D/g, "");
      if (ssn && ssn.length !== 9) {
        this.errors.ssn = "SSN must be 9 digits";
      } else {
        this.errors.ssn = "";
      }
    },
    validateCountry() {
      const country = this.formData.country;
      if (!country) {
        this.errors.country = "Country is required";
      } else if (country.length < 2) {
        this.errors.country = "Please enter a valid country";
      } else {
        this.errors.country = "";
      }
    },
    validateAddressLine1() {
      const address = this.formData.addressLine1;
      if (!address) {
        this.errors.addressLine1 = "Address is required";
      } else if (address.length < 5) {
        this.errors.addressLine1 = "Please enter a valid address";
      } else {
        this.errors.addressLine1 = "";
      }
    },
    validateCity() {
      const city = this.formData.city;
      if (!city) {
        this.errors.city = "City is required";
      } else if (city.length < 2) {
        this.errors.city = "Please enter a valid city";
      } else {
        this.errors.city = "";
      }
    },
    validateState() {
      const state = this.formData.state;
      if (!state) {
        this.errors.state = "State is required";
      } else if (state.length < 2) {
        this.errors.state = "Please enter a valid state";
      } else {
        this.errors.state = "";
      }
    },
    validateZipCode() {
      const zip = this.formData.zipCode;
      if (!zip) {
        this.errors.zipCode = "ZIP code is required";
      } else if (zip.length !== 5) {
        this.errors.zipCode = "ZIP code must be 5 digits";
      } else {
        this.errors.zipCode = "";
      }
    },
    validateDriverLicensePhoto() {
      if (!this.formData.driverLicensePhoto) {
        this.errors.driverLicensePhoto = "Driver license photo is required";
      } else {
        this.errors.driverLicensePhoto = "";
      }
    },
    validateResidenceType() {
      if (!this.formData.residenceType) {
        this.errors.residenceType = "Residence type is required";
      } else {
        this.errors.residenceType = "";
      }
    },
    validateRentPayment() {
      const payment = this.formData.rentPayment;
      if (payment === null || payment === "") {
        this.errors.rentPayment = "Payment amount is required";
      } else if (payment < 0) {
        this.errors.rentPayment = "Payment must be a positive number";
      } else {
        this.errors.rentPayment = "";
      }
    },
    validateEmployer() {
      const employer = this.formData.employer;
      if (!employer) {
        this.errors.employer = "Employer name is required";
      } else if (employer.length < 2) {
        this.errors.employer = "Please enter a valid employer name";
      } else {
        this.errors.employer = "";
      }
    },
    validateJobTitle() {
      const jobTitle = this.formData.jobTitle;
      if (!jobTitle) {
        this.errors.jobTitle = "Job title is required";
      } else if (jobTitle.length < 2) {
        this.errors.jobTitle = "Please enter a valid job title";
      } else {
        this.errors.jobTitle = "";
      }
    },
    validateAnnualIncome() {
      const income = this.formData.annualIncome;
      if (income === null || income === "") {
        this.errors.annualIncome = "Annual income is required";
      } else if (income < 0) {
        this.errors.annualIncome = "Income must be a positive number";
      } else {
        this.errors.annualIncome = "";
      }
    },

    // File Upload Handling
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        this.errors.driverLicensePhoto =
          "Please upload a valid image file (JPG, PNG, WEBP)";
        return;
      }

      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.errors.driverLicensePhoto = "File size must be less than 5MB";
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.photoPreview = e.target.result;
      };
      reader.readAsDataURL(file);

      this.formData.driverLicensePhoto = file;
      this.formData.driverLicensePhotoName = file.name;
      this.errors.driverLicensePhoto = "";
    },

    removeFile() {
      this.formData.driverLicensePhoto = null;
      this.formData.driverLicensePhotoName = "";
      this.photoPreview = null;
      this.$refs.fileInput.value = "";
      this.validateDriverLicensePhoto();
    },

    calculateAge(birthDate) {
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    },

    validateStep1() {
      this.validateName();
      this.validateEmail();
      this.validatePhone();
      this.validateConsent();
      return !this.hasErrors;
    },

    validateStep2() {
      this.validateDateOfBirth();
      this.validateSSN();
      this.validateCountry();
      this.validateAddressLine1();
      this.validateCity();
      this.validateState();
      this.validateZipCode();
      this.validateDriverLicensePhoto();
      this.validateResidenceType();
      this.validateRentPayment();
      this.validateEmployer();
      this.validateJobTitle();
      this.validateAnnualIncome();
      return !this.hasErrors;
    },

    goToNextStep() {
      if (!this.validateStep1()) {
        this.submissionError = "Please fill in all required fields correctly";
        return;
      }
      this.currentStep = 2;
      this.submissionError = null;
      this.$emit("form-validated");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    goBackToStep1() {
      this.currentStep = 1;
      this.submissionError = null;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async submitCompleteForm() {
      if (!this.validateStep2()) {
        this.submissionError = "Please fill in all required fields correctly";
        return;
      }

      this.submitting = true;
      this.submissionError = null;

      try {
        const formDataToSend = new FormData();

        // Basic info
        formDataToSend.append("name", this.formData.name);
        formDataToSend.append("email", this.formData.email);
        formDataToSend.append("phone", this.formData.phone.replace(/\D/g, ""));
        formDataToSend.append("car_id", parseInt(this.carId));

        // Additional info
        formDataToSend.append("date_of_birth", this.formData.dateOfBirth);
        formDataToSend.append("ssn", this.formData.ssn.replace(/\D/g, ""));
        formDataToSend.append("country", this.formData.country);
        formDataToSend.append("address_line1", this.formData.addressLine1);
        formDataToSend.append("address_line2", this.formData.addressLine2);
        formDataToSend.append("city", this.formData.city);
        formDataToSend.append("state", this.formData.state);
        formDataToSend.append("zip_code", this.formData.zipCode);
        formDataToSend.append("residence_type", this.formData.residenceType);
        formDataToSend.append("rent_payment", this.formData.rentPayment);
        formDataToSend.append("employer", this.formData.employer);
        formDataToSend.append("job_title", this.formData.jobTitle);
        formDataToSend.append("annual_income", this.formData.annualIncome);

        // Driver license photo
        if (this.formData.driverLicensePhoto) {
          formDataToSend.append(
            "driver_license_photo",
            this.formData.driverLicensePhoto
          );
        }

        const response = await axios.post(
          `${this.baseUrl}/api/car_request`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 15000,
          }
        );

        if (response.data.success) {
          this.isSuccess = true;
          this.$emit("inquiry-submitted");
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          throw new Error(response.data.error || "Submission failed");
        }
      } catch (error) {
        console.error("Submission error:", error);
        this.submissionError =
          "Failed to submit application. Please try again.";
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.formData = {
        name: "",
        email: "",
        phone: "",
        consent: false,
        carId: this.carId,
        dateOfBirth: "",
        ssn: "",
        country: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        driverLicensePhoto: null,
        driverLicensePhotoName: "",
        residenceType: "",
        rentPayment: null,
        employer: "",
        jobTitle: "",
        annualIncome: null,
      };
      this.errors = {
        name: "",
        email: "",
        phone: "",
        consent: "",
        dateOfBirth: "",
        ssn: "",
        country: "",
        addressLine1: "",
        city: "",
        state: "",
        zipCode: "",
        driverLicensePhoto: "",
        residenceType: "",
        rentPayment: "",
        employer: "",
        jobTitle: "",
        annualIncome: "",
      };
      this.photoPreview = null;
      this.submissionError = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    resetToForm() {
      this.currentStep = 1;
      this.isSuccess = false;
      this.resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>

<style lang="scss" scoped>
.inquiry-form {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1a3c5e;
    margin-bottom: 2rem;
    text-align: center;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -0.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: linear-gradient(to right, #ff6b00, #ff9f43);
      border-radius: 2px;
    }
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;

    .form-group {
      position: relative;

      &.full-width {
        grid-column: 1 / -1;
      }

      &.has-error {
        input,
        select {
          border-color: #ff4444;
          box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
        }
      }

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: #1a3c5e;
      }

      .required {
        color: #ff4444;
        font-size: 0.9em;
      }

      input,
      select {
        width: 100%;
        padding: 1rem;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        font-size: 1rem;
        transition: all 0.3s ease;
        background-color: white;

        &:focus {
          outline: none;
          border-color: #ff6b00;
          box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
        }

        &:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
      }

      select {
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 1rem center;
        padding-right: 2.5rem;
      }
    }

    .error-text {
      color: #ff4444;
      font-size: 0.8rem;
      margin-top: 0.3rem;
      display: block;
      position: absolute;
      bottom: -1.8rem;
      left: 0;
    }
  }

  // File Upload Styles
  .file-upload-wrapper {
    position: relative;

    .file-input {
      display: none;
    }

    .file-upload-label {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 2rem;
      border: 2px dashed #e0e0e0;
      border-radius: 8px;
      background-color: #fafafa;
      cursor: pointer;
      transition: all 0.3s ease;
      color: #666;

      &:hover {
        border-color: #ff6b00;
        background-color: #fff5f0;
      }

      i {
        font-size: 1.5rem;
        color: #ff6b00;
      }

      .file-name {
        color: #1a3c5e;
        font-weight: 500;
      }
    }

    .file-preview {
      margin-top: 1rem;
      position: relative;
      display: inline-block;

      img {
        max-width: 300px;
        max-height: 200px;
        border-radius: 8px;
        border: 2px solid #e0e0e0;
      }

      .remove-file {
        position: absolute;
        top: -8px;
        right: -8px;
        background: #ff4444;
        color: white;
        border: none;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          background: #cc0000;
          transform: scale(1.1);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        i {
          font-size: 1rem;
        }
      }
    }
  }

  .file-info {
    font-size: 0.8rem;
    color: #999;
    margin-top: 0.5rem;
  }

  .form-consent {
    margin-bottom: 2rem;
    text-align: left;

    &.has-error {
      label {
        color: #ff4444;
      }
    }

    label {
      display: flex;
      align-items: center;
      font-size: 0.9rem;
      color: #666;
      cursor: pointer;

      input[type="checkbox"] {
        margin-right: 0.5rem;
        accent-color: #ff6b00;
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }

    .error-text {
      color: #ff4444;
      font-size: 0.8rem;
      margin-top: 0.3rem;
      display: block;
    }
  }

  .error-message {
    color: #ff4444;
    background-color: #ffe6e6;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 0.9rem;
  }

  .button-group {
    display: flex;
    gap: 1rem;
    justify-content: center;
    align-items: center;
  }

  .launch-button,
  .back-button {
    border: none;
    padding: 1rem 2rem;
    font-size: 1.2rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .launch-button {
    background: linear-gradient(135deg, #ff6b00, #ff9f43);
    color: white;

    &:hover:not(:disabled) {
      box-shadow: 0 5px 15px rgba(238, 128, 10, 0.3);
    }
  }

  .back-button {
    background: #e0e0e0;
    color: #333;

    &:hover:not(:disabled) {
      background: #d0d0d0;
    }
  }

  .success-section {
    text-align: center;
    animation: fadeIn 0.3s ease-out;

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .section-icon {
      font-size: 4rem;
      color: #ff6b00;
      margin-bottom: 2rem;
    }

    h3 {
      font-size: 2.2rem;
      color: #1a3c5e;
      margin-bottom: 1.5rem;
    }

    p {
      color: #666;
      font-size: 1.2rem;
      margin-bottom: 2rem;
    }
  }

  .done-button {
    background: #ff6b00;
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

@media (max-width: 992px) {
  .inquiry-form .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .inquiry-form {
    padding: 1rem;

    h2 {
      font-size: 1.8rem !important;
    }

    .file-upload-wrapper {
      .file-upload-label {
        padding: 1.5rem;
        flex-direction: column;
      }

      .file-preview img {
        max-width: 100%;
      }
    }

    .button-group {
      flex-direction: column;
      width: 100%;

      .launch-button,
      .back-button {
        width: 100%;
        justify-content: center;
      }
    }

    .launch-button,
    .back-button {
      padding: 0.8rem 1.5rem;
      font-size: 1rem;
    }

    .success-section {
      .section-icon {
        font-size: 3rem;
        margin-bottom: 1.5rem;
      }

      h3 {
        font-size: 1.8rem;
      }

      p {
        font-size: 1rem;
      }

      .done-button {
        padding: 0.8rem 1.5rem;
        font-size: 1rem;
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style>
